// Portfolio beta: project photos render as the plain photo by default, and as
// a blue ordered-dither (Bayer) halftone while the tile is hovered.
//
// Elm renders <canvas data-mosaic-src="..."> inside each .pb-tile; this module
// paints them. The dither reads pixel values, so it needs the photo host to
// send CORS headers. Without CORS the tile shows the plain photo and never
// dithers.

const DOT_CSS_PX = 4; // dot pitch on screen
const BAYER_4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

const images = new Map();        // src -> Promise<HTMLImageElement | null>
const drawnFrom = new WeakMap(); // canvas -> HTMLImageElement
const seen = new WeakSet();

// Try with CORS first (needed for dithering). If the host refuses, load the
// photo without CORS so it can still be shown, just not dithered.
function loadImage(src) {
  if (!images.has(src)) {
    images.set(src, new Promise((resolve) => {
      const cors = new Image();
      cors.crossOrigin = 'anonymous';
      cors.onload = () => resolve(cors);
      cors.onerror = () => {
        const plain = new Image();
        plain.onload = () => resolve(plain);
        plain.onerror = () => resolve(null);
        plain.src = src;
      };
      cors.src = src;
    }));
  }
  return images.get(src);
}

// Centred crop that matches the target aspect ratio (like object-fit: cover).
function coverCrop(img, aspect) {
  const imgAspect = img.naturalWidth / img.naturalHeight;
  let sx = 0;
  let sy = 0;
  let sw = img.naturalWidth;
  let sh = img.naturalHeight;
  if (imgAspect > aspect) {
    sw = img.naturalHeight * aspect;
    sx = (img.naturalWidth - sw) / 2;
  } else {
    sh = img.naturalWidth / aspect;
    sy = (img.naturalHeight - sh) / 2;
  }
  return { sx, sy, sw, sh };
}

function sizeCanvas(canvas) {
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(rect.width * dpr);
  canvas.height = Math.round(rect.height * dpr);
  return dpr;
}

function drawPhoto(canvas, img) {
  const ctx = canvas.getContext('2d');
  const { sx, sy, sw, sh } = coverCrop(img, canvas.width / canvas.height);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
}

// One palette per site accent (matches .red-border / .green-border / .blue-border).
// Each has a dark background, a dark-to-mid ramp, and a pale highlight.
const PALETTES = {
  'blue-border': { bg: [5, 10, 31], dark: [20, 60, 184], mid: [60, 123, 255], high: [207, 226, 255] },
  'red-border': { bg: [20, 5, 5], dark: [120, 40, 40], mid: [193, 80, 80], high: [255, 220, 220] },
  'green-border': { bg: [3, 20, 16], dark: [0, 90, 76], mid: [0, 160, 135], high: [200, 255, 240] },
};

function paletteFor(canvas) {
  const tile = canvas.closest('.pb-tile');
  const accent = Object.keys(PALETTES).find((name) => tile && tile.classList.contains(name));
  return PALETTES[accent || 'blue-border'];
}

function mix(a, b, k) {
  return a.map((v, i) => Math.round(v + (b[i] - v) * k));
}

// Brightness 0..1 -> color along the tile's palette.
function dotColor(palette, t) {
  const rgb = t < 0.6
    ? mix(palette.dark, palette.mid, t / 0.6)
    : mix(palette.mid, palette.high, (t - 0.6) / 0.4);
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

// Returns false when the photo can't be read, so the caller falls back to the photo.
function drawDither(canvas, img) {
  const dpr = sizeCanvas(canvas);
  if (!dpr) return true;

  const cell = DOT_CSS_PX * dpr;
  const cols = Math.max(1, Math.round(canvas.width / cell));
  const rows = Math.max(1, Math.round(canvas.height / cell));
  const cellW = canvas.width / cols;
  const cellH = canvas.height / rows;

  // Downscale to one pixel per dot; the browser averages while scaling.
  const { sx, sy, sw, sh } = coverCrop(img, canvas.width / canvas.height);
  const small = document.createElement('canvas');
  small.width = cols;
  small.height = rows;
  const smallCtx = small.getContext('2d', { willReadFrequently: true });
  smallCtx.drawImage(img, sx, sy, sw, sh, 0, 0, cols, rows);

  let pixels;
  try {
    pixels = smallCtx.getImageData(0, 0, cols, rows).data;
  } catch (err) {
    return false; // tainted: no CORS on the photo host
  }

  const palette = paletteFor(canvas);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = `rgb(${palette.bg.join(', ')})`;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const radius = Math.min(cellW, cellH) * 0.42;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = (r * cols + c) * 4;
      const lum = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
      // Ordered dither: a dot is on with probability proportional to brightness.
      const threshold = ((BAYER_4[(r % 4) * 4 + (c % 4)] + 0.5) / 16) * 255;
      if (lum <= threshold) continue;

      const t = lum / 255;
      ctx.fillStyle = dotColor(palette, t);
      ctx.shadowBlur = t > 0.75 ? 6 * dpr : 0;
      ctx.shadowColor = dotColor(palette, t);
      ctx.beginPath();
      ctx.arc(c * cellW + cellW / 2, r * cellH + cellH / 2, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.shadowBlur = 0;
  return true;
}

function redraw(canvas) {
  const img = drawnFrom.get(canvas);
  if (!img) return;
  if (canvas.dataset.mode === 'dither') {
    sizeCanvas(canvas);
    if (drawDither(canvas, img)) return;
  }
  sizeCanvas(canvas);
  drawPhoto(canvas, img);
}

// Redraw whenever the canvas changes size (layout, breakpoints, window resize).
const resizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const canvas = entry.target;
    if (!canvas.isConnected) {
      resizeObserver.unobserve(canvas);
      continue;
    }
    redraw(canvas);
  }
});

function render(canvas) {
  if (seen.has(canvas)) return;
  seen.add(canvas);
  canvas.dataset.mode = 'photo';

  const tile = canvas.closest('.pb-tile');
  if (tile) {
    tile.addEventListener('pointerenter', () => {
      canvas.dataset.mode = 'dither';
      redraw(canvas);
    });
    tile.addEventListener('pointerleave', () => {
      canvas.dataset.mode = 'photo';
      redraw(canvas);
    });
  }

  loadImage(canvas.dataset.mosaicSrc).then((img) => {
    if (!img) return; // leave the dark background if the photo fails to load
    drawnFrom.set(canvas, img);
    resizeObserver.observe(canvas);
  });
}

function scan(root) {
  root.querySelectorAll('canvas[data-mosaic-src]').forEach(render);
}

function start() {
  scan(document);
  // Elm re-renders the page on navigation, so pick up canvases as they appear.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('canvas[data-mosaic-src]')) render(node);
        scan(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
